import { Fragment } from 'react'
import { colors, FITPET_CDN_URL } from '@fitpet/constants-global'
import { FitpetIcon } from '@fitpet/ui/components'
import { StyledDivider } from '@/components/common/divider'
import { HorizontalScrollContainer } from '@/components/domains/etc/horizontal-scroll-container'
import { HomeSectionTitle } from '@/components/domains/home'
import { Anchor } from '@/components/domains/home/Anchor'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { HomeSectionTitleEnum } from '@/constants/home-section-title.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { LocationEnum } from '@/constants/location.enum'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useHomeBrandShopTracker } from '@/containers/event-tracker/home-brand-shop.tracker'
import { useCustomRouter, useIntersectionObserver } from '@/containers/hooks'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import type {
  BrandResponseDto,
  HomeSectionBrandResponseDtoV2,
  ProductsListItemResponseDto,
} from '@/apis/rest/generated/types'
import type { AdPetType } from '@/graphql/generated/schemas'
import styles from './home-section-brand.module.css'

interface HomeSectionBrandProps {
  petType: AdPetType
  homeSectionItem: HomeSectionBrandResponseDtoV2
  refCallback: (node: HTMLDivElement) => void
}

export const HomeSectionBrand = ({ petType, homeSectionItem, refCallback }: HomeSectionBrandProps) => {
  const router = useCustomRouter()
  const { basePath, asPath } = useCustomRouter()
  const { trackClickHomeBrandShopItemEvent } = useHomeBrandShopTracker()

  const handleClickProduct = (brand: BrandResponseDto) => {
    trackClickHomeBrandShopItemEvent({ brandId: brand.id, brandName: brand.name, type: 'Item' })
  }

  const handleClickMoreButton = (brand: BrandResponseDto) => {
    const nyangSsamId = 24
    const adSlug = 'B_nyangssaem_main_cat'
    const currentPath = basePath + asPath
    if (brand.id === nyangSsamId) {
      router.push({
        pathname: ROUTES.EVENT_DETAIL,
        query: { adSlug: adSlug, location: LocationEnum.ProductDetail, [QueryStringKeyEnum.PrevPath]: currentPath },
      })
      return
    }

    const brandId = Base64ConverterUtil.convertNumberIdToBase64ByKey({ key: 'BrandType', id: brand.id })

    router.push({
      pathname: ROUTES.BRAND_DETAIL,
      query: {
        brandId,
        location: LocationEnum.GnbHome,
      },
    })
  }

  const { brandsWithProducts } = homeSectionItem
  if (!brandsWithProducts || brandsWithProducts.length === 0) {
    return null
  }

  return (
    <div key={petType}>
      <Anchor id={HomeSectionTitleEnum.BrandShop} refCallback={refCallback} />
      <HomeSectionTitle>핏펫 MADE</HomeSectionTitle>
      <HorizontalScrollContainer>
        <div className={styles.gridBlockDiv}>
          {brandsWithProducts.map(({ brand, products }, index) => (
            <ProductWithBrand
              key={brand.id}
              index={index}
              brand={brand}
              products={products}
              onClickProductCard={() => handleClickProduct(brand)}
              onClickMoreButton={() => handleClickMoreButton(brand)}
            />
          ))}
        </div>
      </HorizontalScrollContainer>
      <StyledDivider height="0.1rem" backgroundColor={colors.GRAY_70} />
    </div>
  )
}

interface ProductWithBrandProps {
  index: number
  brand: BrandResponseDto
  products: ProductsListItemResponseDto[]
  onClickProductCard: (brand: BrandResponseDto) => void
  onClickMoreButton: (brand: BrandResponseDto) => void
}

const ProductWithBrand = ({ index, brand, products, onClickProductCard, onClickMoreButton }: ProductWithBrandProps) => {
  const { trackViewHomeBrandShopBrandEvent } = useHomeBrandShopTracker()

  const { elementRef } = useIntersectionObserver(
    () => trackViewHomeBrandShopBrandEvent({ brandId: brand.id, brandName: brand.name, type: 'Brand' }),
    { rootMargin: '0px', threshold: 1 }
  )

  return (
    <div ref={elementRef} className={styles.productBlockDiv}>
      {brand.homeImagePath && (
        <img
          className={styles.bannerImg}
          alt={brand.name || `home-section-brand-name-${index}`}
          src={`${FITPET_CDN_URL}/${brand.homeImagePath}`}
        />
      )}
      <div className={styles.productWrapperDiv}>
        {products.map((product, index, ar) => {
          const data = transformProductTypeDefinedByFrontend(product)

          return (
            <Fragment key={product.id}>
              <div key={product.id} className={styles.productCardWrapper}>
                <NewProductCard
                  product={data}
                  size="md"
                  cartType="HORIZONTAL"
                  onClickProductCard={() => onClickProductCard(brand)}
                />
              </div>
              {index < ar.length - 1 && (
                <StyledDivider height="0.1rem" backgroundColor={colors.GRAY_70} className={styles.brandDivider} />
              )}
            </Fragment>
          )
        })}
      </div>
      <button type="button" onClick={() => onClickMoreButton(brand)} className={styles.moreButton}>
        <div className="font-body2-medium gray-900">{brand.name ? `${brand.name} 더 보기` : '더 보기'}</div>
        <FitpetIcon icon="icChevronRight" size={16} color={colors.GRAY_700} />
      </button>
    </div>
  )
}
